import React, { useMemo, useState } from 'react';
import {
  Check,
  Target,
  ChevronRight,
  AlertCircle,
  Download,
  Maximize2,
  Minimize2,
  Info,
  Loader2
} from 'lucide-react';
import { useCisaFrameworkCompat } from '../hooks/useCisaReferenceData';
import './CisaMatrixView.css';

/**
 * CISA Matrix View - Workshop Assessment Deliverable
 *
 * Displays the complete CISA Zero Trust Maturity Model assessment
 * in a single visual matrix format. This is the "final deliverable"
 * view for 2-hour consulting workshops with Security Architects.
 *
 * Features:
 * - All 5 pillars displayed as column sections
 * - All functions listed as rows within each pillar
 * - 4 maturity levels as sub-columns (Traditional → Optimal)
 * - Current assessment level highlighted
 * - Target level indicated with border/icon
 * - Gap visualization between current and target
 * - Click to drill down into function details
 * - Export-ready layout for PDF/screenshots
 */

const MATURITY_LEVELS = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
const MATURITY_LABELS = {
  TRADITIONAL: 'Traditional',
  INITIAL: 'Initial',
  ADVANCED: 'Advanced',
  OPTIMAL: 'Optimal'
};

/**
 * Individual cell in the matrix
 */
function MatrixCell({
  functionCode,
  level,
  currentLevel,
  targetLevel,
  onClick
}) {
  const isCurrent = currentLevel === level;
  const isTarget = targetLevel === level;
  const isGap = targetLevel &&
    MATURITY_LEVELS.indexOf(level) > MATURITY_LEVELS.indexOf(currentLevel) &&
    MATURITY_LEVELS.indexOf(level) <= MATURITY_LEVELS.indexOf(targetLevel);
  const isPast = MATURITY_LEVELS.indexOf(level) < MATURITY_LEVELS.indexOf(currentLevel);

  const cellClasses = [
    'cmv-cell',
    `cmv-cell--${level.toLowerCase()}`,
    isCurrent && 'cmv-cell--current',
    isTarget && 'cmv-cell--target',
    isGap && 'cmv-cell--gap',
    isPast && 'cmv-cell--past'
  ].filter(Boolean).join(' ');

  return (
    <td
      className={cellClasses}
      onClick={() => onClick?.(functionCode, level)}
      title={`${MATURITY_LABELS[level]}${isCurrent ? ' (Current)' : ''}${isTarget ? ' (Target)' : ''}`}
    >
      <div className="cmv-cell-content">
        {isCurrent && (
          <span className="cmv-cell-indicator cmv-cell-indicator--current">
            <Check size={12} />
          </span>
        )}
        {isTarget && !isCurrent && (
          <span className="cmv-cell-indicator cmv-cell-indicator--target">
            <Target size={12} />
          </span>
        )}
      </div>
    </td>
  );
}

/**
 * Single pillar section in the matrix
 */
function PillarSection({
  pillar,
  functions,
  assessments,
  targetLevels,
  onCellClick,
  showCrossCutting = false
}) {
  // Filter to only show core functions (not cross-cutting) unless explicitly requested
  const displayFunctions = showCrossCutting
    ? functions
    : functions.filter(f => !f.isCrossCutting);

  return (
    <div className="cmv-pillar">
      <div
        className="cmv-pillar-header"
        style={{ borderTopColor: pillar.color }}
      >
        <span className="cmv-pillar-name">{pillar.name}</span>
        <span className="cmv-pillar-count">
          {displayFunctions.length} functions
        </span>
      </div>

      <table className="cmv-pillar-table">
        <thead>
          <tr>
            <th className="cmv-function-col">Function</th>
            {MATURITY_LEVELS.map(level => (
              <th
                key={level}
                className={`cmv-level-col cmv-level-col--${level.toLowerCase()}`}
              >
                {MATURITY_LABELS[level].charAt(0)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayFunctions.map(fn => (
            <tr key={fn.code} className="cmv-function-row">
              <td className="cmv-function-name">
                <span className="cmv-function-label">{fn.name}</span>
                {fn.isNew && <span className="cmv-badge cmv-badge--new">New</span>}
                {fn.isCrossCutting && (
                  <span className="cmv-badge cmv-badge--cc">CC</span>
                )}
              </td>
              {MATURITY_LEVELS.map(level => (
                <MatrixCell
                  key={level}
                  functionCode={fn.code}
                  level={level}
                  currentLevel={assessments[fn.code] || 'TRADITIONAL'}
                  targetLevel={targetLevels[fn.code]}
                  onClick={onCellClick}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Cross-cutting capabilities section
 */
function CrossCuttingSection({ crossCutting, assessments, targetLevels, onCellClick }) {
  return (
    <div className="cmv-pillar cmv-pillar--cross-cutting">
      <div className="cmv-pillar-header cmv-pillar-header--cc">
        <span className="cmv-pillar-name">Cross-Cutting Capabilities</span>
        <span className="cmv-pillar-count">{crossCutting.length} capabilities</span>
      </div>

      <table className="cmv-pillar-table">
        <thead>
          <tr>
            <th className="cmv-function-col">Capability</th>
            {MATURITY_LEVELS.map(level => (
              <th
                key={level}
                className={`cmv-level-col cmv-level-col--${level.toLowerCase()}`}
              >
                {MATURITY_LABELS[level].charAt(0)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {crossCutting.map(cc => (
            <tr key={cc.code} className="cmv-function-row">
              <td className="cmv-function-name">
                <span className="cmv-function-label">{cc.name}</span>
              </td>
              {MATURITY_LEVELS.map(level => (
                <MatrixCell
                  key={level}
                  functionCode={cc.code}
                  level={level}
                  currentLevel={assessments[cc.code] || 'TRADITIONAL'}
                  targetLevel={targetLevels[cc.code]}
                  onClick={onCellClick}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Summary statistics bar
 */
function MatrixSummary({ coreFunctions, crossCutting, assessments, targetLevels }) {
  const stats = useMemo(() => {
    const allFunctions = Object.values(coreFunctions).flat();
    const totalFunctions = allFunctions.length + crossCutting.length;

    let traditional = 0;
    let initial = 0;
    let advanced = 0;
    let optimal = 0;
    let withGaps = 0;

    const countLevel = (code) => {
      const level = assessments[code] || 'TRADITIONAL';
      const target = targetLevels[code];

      if (level === 'TRADITIONAL') traditional++;
      else if (level === 'INITIAL') initial++;
      else if (level === 'ADVANCED') advanced++;
      else if (level === 'OPTIMAL') optimal++;

      if (target && MATURITY_LEVELS.indexOf(target) > MATURITY_LEVELS.indexOf(level)) {
        withGaps++;
      }
    };

    allFunctions.forEach(f => countLevel(f.code));
    crossCutting.forEach(cc => countLevel(cc.code));

    // Calculate overall maturity score (1-4 scale)
    const score = (traditional * 1 + initial * 2 + advanced * 3 + optimal * 4) / totalFunctions;

    return {
      totalFunctions,
      traditional,
      initial,
      advanced,
      optimal,
      withGaps,
      score: score.toFixed(1),
      scoreLabel: score < 1.5 ? 'Traditional' : score < 2.5 ? 'Initial' : score < 3.5 ? 'Advanced' : 'Optimal'
    };
  }, [coreFunctions, crossCutting, assessments, targetLevels]);

  return (
    <div className="cmv-summary">
      <div className="cmv-summary-score">
        <span className="cmv-summary-score-value">{stats.score}</span>
        <span className="cmv-summary-score-label">{stats.scoreLabel}</span>
      </div>

      <div className="cmv-summary-bars">
        <div className="cmv-summary-bar">
          <span
            className="cmv-summary-bar-fill cmv-summary-bar-fill--traditional"
            style={{ width: `${(stats.traditional / stats.totalFunctions) * 100}%` }}
          />
          <span
            className="cmv-summary-bar-fill cmv-summary-bar-fill--initial"
            style={{ width: `${(stats.initial / stats.totalFunctions) * 100}%` }}
          />
          <span
            className="cmv-summary-bar-fill cmv-summary-bar-fill--advanced"
            style={{ width: `${(stats.advanced / stats.totalFunctions) * 100}%` }}
          />
          <span
            className="cmv-summary-bar-fill cmv-summary-bar-fill--optimal"
            style={{ width: `${(stats.optimal / stats.totalFunctions) * 100}%` }}
          />
        </div>
      </div>

      <div className="cmv-summary-stats">
        <div className="cmv-summary-stat">
          <span className="cmv-stat-value">{stats.totalFunctions}</span>
          <span className="cmv-stat-label">Functions</span>
        </div>
        <div className="cmv-summary-stat cmv-summary-stat--gap">
          <span className="cmv-stat-value">{stats.withGaps}</span>
          <span className="cmv-stat-label">Gaps</span>
        </div>
        <div className="cmv-summary-stat cmv-summary-stat--optimal">
          <span className="cmv-stat-value">{stats.optimal}</span>
          <span className="cmv-stat-label">Optimal</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Detail drawer for selected function
 */
function FunctionDetailDrawer({ functionCode, maturityCriteria, onClose }) {
  if (!functionCode) return null;

  const criteria = maturityCriteria[functionCode] || {};

  return (
    <div className="cmv-drawer">
      <div className="cmv-drawer-header">
        <h3>{functionCode}</h3>
        <button type="button" onClick={onClose} className="cmv-drawer-close">
          &times;
        </button>
      </div>
      <div className="cmv-drawer-content">
        {MATURITY_LEVELS.map(level => (
          <div key={level} className="cmv-drawer-level">
            <h4 className={`cmv-drawer-level-title cmv-level--${level.toLowerCase()}`}>
              {MATURITY_LABELS[level]}
            </h4>
            <p className="cmv-drawer-level-text">
              {criteria[level] || 'No criteria defined.'}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Main CisaMatrixView Component
 */
const CisaMatrixView = ({
  assessments = {},
  targetLevels = {},
  onCellClick,
  onExport,
  showCrossCutting = false,
  className = ''
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedFunction, setSelectedFunction] = useState(null);

  // Fetch CISA framework data from database
  const { data: frameworkData, isLoading, error } = useCisaFrameworkCompat();

  // Extract data from hook (with fallbacks for loading state)
  const CISA_PILLARS = frameworkData?.CISA_PILLARS || [];
  const CISA_CORE_FUNCTIONS = frameworkData?.CISA_CORE_FUNCTIONS || {};
  const CROSS_CUTTING = frameworkData?.CROSS_CUTTING || [];
  const MATURITY_CRITERIA = frameworkData?.MATURITY_CRITERIA || {};

  const handleCellClick = (functionCode, level) => {
    if (onCellClick) {
      onCellClick(functionCode, level);
    } else {
      setSelectedFunction(functionCode);
    }
  };

  const handleExport = () => {
    if (onExport) {
      onExport();
    } else {
      // Default: trigger print dialog for PDF export
      window.print();
    }
  };

  const containerClasses = [
    'cmv-container',
    isFullscreen && 'cmv-container--fullscreen',
    className
  ].filter(Boolean).join(' ');

  // Loading state
  if (isLoading) {
    return (
      <div className={containerClasses}>
        <div className="cmv-loading">
          <Loader2 className="cmv-loading-spinner" size={32} />
          <span>Loading CISA Framework...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={containerClasses}>
        <div className="cmv-error">
          <AlertCircle size={32} />
          <span>Failed to load CISA Framework</span>
          <p>{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      {/* Header */}
      <div className="cmv-header">
        <div className="cmv-header-left">
          <h2 className="cmv-title">CISA Zero Trust Maturity Assessment</h2>
          <span className="cmv-subtitle">Complete posture across all pillars</span>
        </div>

        <div className="cmv-header-right">
          <button
            type="button"
            className="cmv-action-btn"
            onClick={handleExport}
            title="Export to PDF"
          >
            <Download size={16} />
            <span>Export</span>
          </button>
          <button
            type="button"
            className="cmv-action-btn"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <MatrixSummary
        coreFunctions={CISA_CORE_FUNCTIONS}
        crossCutting={CROSS_CUTTING}
        assessments={assessments}
        targetLevels={targetLevels}
      />

      {/* Legend */}
      <div className="cmv-legend">
        <div className="cmv-legend-item">
          <span className="cmv-legend-swatch cmv-legend-swatch--current">
            <Check size={10} />
          </span>
          <span>Current Level</span>
        </div>
        <div className="cmv-legend-item">
          <span className="cmv-legend-swatch cmv-legend-swatch--target">
            <Target size={10} />
          </span>
          <span>Target Level</span>
        </div>
        <div className="cmv-legend-item">
          <span className="cmv-legend-swatch cmv-legend-swatch--gap" />
          <span>Gap</span>
        </div>
        <div className="cmv-legend-divider" />
        <div className="cmv-legend-levels">
          {MATURITY_LEVELS.map(level => (
            <div key={level} className="cmv-legend-level">
              <span
                className={`cmv-legend-level-dot cmv-level--${level.toLowerCase()}`}
              />
              <span>{MATURITY_LABELS[level]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Matrix grid */}
      <div className="cmv-matrix">
        {/* Pillar sections */}
        {CISA_PILLARS.map(pillar => (
          <PillarSection
            key={pillar.code}
            pillar={pillar}
            functions={CISA_CORE_FUNCTIONS[pillar.code] || []}
            assessments={assessments}
            targetLevels={targetLevels}
            onCellClick={handleCellClick}
            showCrossCutting={showCrossCutting}
          />
        ))}

        {/* Cross-cutting capabilities */}
        <CrossCuttingSection
          crossCutting={CROSS_CUTTING}
          assessments={assessments}
          targetLevels={targetLevels}
          onCellClick={handleCellClick}
        />
      </div>

      {/* Footer */}
      <div className="cmv-footer">
        <span className="cmv-footer-ref">
          Based on CISA Zero Trust Maturity Model v2.0 (April 2023)
        </span>
        <a
          href="https://www.cisa.gov/zero-trust-maturity-model"
          target="_blank"
          rel="noopener noreferrer"
          className="cmv-footer-link"
        >
          <Info size={12} />
          Learn more
        </a>
      </div>

      {/* Detail drawer */}
      <FunctionDetailDrawer
        functionCode={selectedFunction}
        maturityCriteria={MATURITY_CRITERIA}
        onClose={() => setSelectedFunction(null)}
      />
    </div>
  );
};

export default CisaMatrixView;
